/**
 * Permission Service
 * API calls for permission listing (read-only)
 */

import apiClient from './apiClient';
import {
  Permission,
  PermissionListResponse,
  PermissionResponse,
  PermissionFilters,
  PaginationOptions,
  PermissionsByModuleResponse,
  AvailableModulesResponse,
  AvailableActionsResponse,
} from '../types/rbac.types';

/**
 * Get all permissions with filters and pagination
 */
export const getPermissions = async (
  filters?: PermissionFilters,
  pagination?: PaginationOptions
): Promise<PermissionListResponse> => {
  const params = new URLSearchParams();

  // Add filters
  if (filters?.search) params.append('search', filters.search);
  if (filters?.guardName) params.append('guardName', filters.guardName);
  if (filters?.module) params.append('module', filters.module);
  if (filters?.action) params.append('action', filters.action);
  if (filters?.resource) params.append('resource', filters.resource);
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());

  // Add pagination
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());
  if (pagination?.sortBy) params.append('sortBy', pagination.sortBy);
  if (pagination?.sortOrder) params.append('sortOrder', pagination.sortOrder);

  const response = await apiClient.get<PermissionListResponse>(`/permissions?${params.toString()}`);
  return response as unknown as PermissionListResponse;
};

/**
 * Get permission by ID
 */
export const getPermissionById = async (id: number): Promise<PermissionResponse> => {
  const response = await apiClient.get<PermissionResponse>(`/permissions/${id}`);
  return response as unknown as PermissionResponse;
};

/**
 * Get permissions grouped by module
 */
export const getPermissionsByModule = async (
  filters?: Pick<PermissionFilters, 'isActive'>
): Promise<PermissionsByModuleResponse> => {
  const params = new URLSearchParams();
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());

  const response = await apiClient.get<PermissionsByModuleResponse>(
    `/permissions/by-module?${params.toString()}`
  );
  return response as unknown as PermissionsByModuleResponse;
};

/**
 * Get roles that have specific permission
 */
export const getPermissionRoles = async (permissionId: number): Promise<any> => {
  const response = await apiClient.get(`/permissions/${permissionId}/roles`);
  return response;
};

/**
 * Get all active permissions (for dropdowns)
 */
export const getActivePermissions = async (): Promise<{ message: string; data: Permission[] }> => {
  const response = await apiClient.get<{ message: string; data: Permission[] }>('/permissions/active');
  return response as unknown as { message: string; data: Permission[] };
};

/**
 * Get available modules
 */
export const getAvailableModules = async (): Promise<AvailableModulesResponse> => {
  const response = await apiClient.get<AvailableModulesResponse>('/permissions/modules');
  return response as unknown as AvailableModulesResponse;
};

/**
 * Get available actions
 */
export const getAvailableActions = async (): Promise<AvailableActionsResponse> => {
  const response = await apiClient.get<AvailableActionsResponse>('/permissions/actions');
  return response as unknown as AvailableActionsResponse;
};

export default {
  getPermissions,
  getPermissionById,
  getPermissionsByModule,
  getPermissionRoles,
  getActivePermissions,
  getAvailableModules,
  getAvailableActions,
};
